import { MonthlyCheckDto } from "@/pages/monthly-inspection";
import { monthlyCheckService } from "@/services/monthlyCheckService";
import {
  ApiResponse,
  CommonParams,
  DEFAULT_ERROR_MESSAGE,
  DEFAULT_SUCCESS_MESSAGE,
} from "@/types";
import { toastError, toastSuccess } from "@/utils";
import { create } from "zustand";
import { useAuthStore } from "./authStore";

type MonthlyCheckStoreType = {
  monthlyChecks: ApiResponse<MonthlyCheckDto[]>;
  monthlyCheck: MonthlyCheckDto | undefined;
  lastFetchTime: number | null;
  lastParams?: CommonParams<{ theme?: string }>;
  isFetching: boolean;
  fetchMonthlyChecks: (
    params: CommonParams<{ theme?: string }>,
    cacheTimeMs?: number
  ) => Promise<ApiResponse<MonthlyCheckDto[]>>;
  fetchMonthlyCheckById: (id: number) => MonthlyCheckDto | undefined;
  answerMonthlyCheck: (data: {
    result_id: number;
    answers: boolean[];
  }) => Promise<ApiResponse<null>>;
  loading: boolean;
  error: string | null;
};

export const useMonthlyCheckStore = create<MonthlyCheckStoreType>(
  (set, get) => ({
    loading: false,
    error: null,
    monthlyChecks: { data: [], status: 200 },
    monthlyCheck: undefined,
    lastFetchTime: null,
    isFetching: false,
    fetchMonthlyChecks: async (
      params: CommonParams<{ theme?: string }>,
      cacheTimeMs = 1 * 60 * 1000
    ) => {
      const { monthlyChecks, lastFetchTime, isFetching, lastParams } = get();
      const { user } = useAuthStore.getState();
      const now = Date.now();

      if (isFetching) return monthlyChecks;

      const paramsWithUser = {
        ...params,
        userId: user?.id || null,
      };

      const paramsChanged =
        JSON.stringify(lastParams) !== JSON.stringify(paramsWithUser);

      if (
        !paramsChanged &&
        monthlyChecks.data.length > 0 &&
        lastFetchTime &&
        now - lastFetchTime < cacheTimeMs
      ) {
        return monthlyChecks;
      }

      set({ loading: true, isFetching: true, error: null });
      try {
        const response = await monthlyCheckService.getMonthlyChecks(params);
        // Ensure data is always an array
        const safeResponse = {
          ...response,
          data: Array.isArray(response.data) ? response.data : [],
        };
        set({
          monthlyChecks: safeResponse,
          loading: false,
          isFetching: false,
          lastFetchTime: now,
          lastParams: paramsWithUser,
        });
        return response;
      } catch (error: any) {
        const errorMsg = error.message || DEFAULT_ERROR_MESSAGE;
        set({ loading: false, isFetching: false, error: errorMsg });

        toastError(errorMsg);

        return monthlyChecks.data.length > 0
            ? monthlyChecks
            : ({
              data: [],
              pagination: {
                page: 1,
                pageSize: 10,
                total: 0,
                totalPages: 0,
              },
            } as any);
      }
    },

    fetchMonthlyCheckById: (id: number) => {
      const { monthlyChecks } = get();
      const monthlyCheckById = monthlyChecks.data.find(
        (monthlyCheck) => monthlyCheck.result_id === id
      );

      set({ monthlyCheck: monthlyCheckById });

      return monthlyCheckById;
    },

    answerMonthlyCheck: async (data) => {
      try {
        const response = await monthlyCheckService.answerMonthlyCheck(data);

        const { monthlyChecks, monthlyCheck } = get();
        const checkId = Number(data.result_id);

        const updatedMonthlyChecks = monthlyChecks.data.map((item) =>
          item.result_id === checkId
            ? { ...item, has_answered: true, current_answers: data.answers }
            : item
        );

        const updatedMonthlyCheck =
          monthlyCheck?.result_id === checkId
            ? {
                ...monthlyCheck,
                has_answered: true,
                current_answers: data.answers,
              }
            : monthlyCheck;

        set({
          monthlyChecks: {
            ...monthlyChecks,
            data: updatedMonthlyChecks,
          },
          monthlyCheck: updatedMonthlyCheck,
        });

        toastSuccess(response.message || DEFAULT_SUCCESS_MESSAGE);

        return response;
      } catch (error: any) {
        toastError(error?.message || DEFAULT_ERROR_MESSAGE);

        throw error;
      }
    },
  })
);
